import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    HStack,
    IconButton,
    Image,
    SimpleGrid,
    Spinner,
    Stack,
    Text,
    VStack,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import axios from "axios";
import { useCombinedStore } from "../../store/hooks/useCombinedStore";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { getOrderTracking } from "./orderTracking";

const fallbackImage = "https://via.placeholder.com/800x500?text=Order+Item";
const ORDERS_API = process.env.REACT_APP_BACKEND_URL + "/api/v1/orders";
const cardsBorderRadii = "8px";

const OrderDetail = () => {
    const { id } = useParams<{ id: string; }>();
    const navigate = useNavigate();
    const { state } = useLocation() as { state?: { order?: any; }; };
    const { orders } = useCombinedStore();
    const { theme } = useCustomTheme();

    const [loading, setLoading] = useState(true);
    const [fetchedOrder, setFetchedOrder] = useState<any | null>(null);
    const [activeImageIndex, setActiveImageIndex] = useState<Record<number, number>>({});

    const stateOrder = state?.order ?? null;

    const matchesOrderId = (value: any, currentId?: string) => {
        if (!value || !currentId) return false;
        return String(value?._id ?? value?.id) === String(currentId);
    };

    const isRichOrder = (value: any) => {
        if (!value) return false;

        return Boolean(
            value.total != null ||
            value.status != null ||
            value.email != null ||
            value.phoneNumber != null ||
            value.fromAddress != null ||
            value.toAddress != null ||
            value.deliveryInstructions != null ||
            (Array.isArray(value.items) && value.items.length > 0)
        );
    };

    const orderFromStore = useMemo(
        () => orders.find((o: any) => matchesOrderId(o, id)),
        [orders, id]
    );

    const matchingStateOrder = useMemo(() => {
        return matchesOrderId(stateOrder, id) ? stateOrder : null;
    }, [stateOrder, id]);

    const matchingFetchedOrder = useMemo(() => {
        return matchesOrderId(fetchedOrder, id) ? fetchedOrder : null;
    }, [fetchedOrder, id]);

    const pickBestOrder = (...candidates: any[]) => {
        const validCandidates = candidates.filter(Boolean);
        const rich = validCandidates.filter(isRichOrder);

        if (rich.length > 0) {
            return rich[rich.length - 1];
        }

        return validCandidates[validCandidates.length - 1] || null;
    };

    useEffect(() => {
        setFetchedOrder(null);
        setActiveImageIndex({});
        setLoading(true);
    }, [id]);

    useEffect(() => {
        if (!id) {
            setLoading(false);
            return;
        }

        const shouldFetch = !isRichOrder(matchingFetchedOrder);

        if (!shouldFetch) {
            setLoading(false);
            return;
        }

        let isMounted = true;

        const run = async () => {
            setLoading(true);

            try {
                const res = await axios.get(`${ORDERS_API}/${id}`);
                const fullOrder = res.data?.data || res.data?.order || res.data || null;

                if (isMounted && matchesOrderId(fullOrder, id)) {
                    setFetchedOrder(fullOrder);
                } else if (isMounted) {
                    setFetchedOrder(null);
                }
            } catch (err) {
                console.error("Failed to fetch order by id:", err);
                if (isMounted) {
                    setFetchedOrder(null);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        run();

        return () => {
            isMounted = false;
        };
    }, [id, matchingStateOrder, orderFromStore, matchingFetchedOrder]);

    const order = useMemo(() => {
        return pickBestOrder(matchingStateOrder, orderFromStore, matchingFetchedOrder);
    }, [matchingStateOrder, orderFromStore, matchingFetchedOrder]);

    const currency = order?.currency || "MWK";
    const tracking = getOrderTracking({
        ...order,
        serviceCategory: order?.serviceCategory || order?.category || order?.orderType,
        paymentStatus: order?.payment?.status || order?.paymentStatus,
    });
    const hideItemImages = [
        order?.serviceCategory, order?.category, order?.orderType,
        order?.metadata?.serviceCategory, order?.metadata?.category, order?.metadata?.orderType,
        ...(order?.items || []).flatMap((item: any) => [item?.serviceCategory, item?.category, item?.orderType]),
    ].some((value) => ["delivery", "courier", "direct_delivery", "a_to_b", "trip", "trips", "hike", "hikes", "travel", "tour"].includes(
        String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_"),
    ));

    const formatMoney = (value: any) => {
        const num = Number(value || 0);
        return `${currency} ${num.toLocaleString()}`;
    };

    const getStatusBadge = () => {
        const raw = tracking.status;

        if (raw.includes("completed") || raw.includes("paid") || raw.includes("success")) {
            return { label: tracking.label, colorScheme: "green" as const };
        }

        if (raw.includes("pending")) {
            return { label: tracking.label, colorScheme: "yellow" as const };
        }

        if (raw.includes("cancel") || raw.includes("failed")) {
            return { label: tracking.label, colorScheme: "red" as const };
        }

        if (raw.includes("shipped") || raw.includes("dispatch") || raw.includes("transit")) {
            return { label: tracking.label, colorScheme: "blue" as const };
        }

        if (raw.includes("delivered")) {
            return { label: tracking.label, colorScheme: "green" as const };
        }

        return { label: tracking.label, colorScheme: "gray" as const };
    };

    const statusBadge = getStatusBadge();

    const getPaymentSummary = () => {
        const payment = order?.payment || {};

        const cardNumber = String(payment?.cardNumber || "").trim();
        const cardHolder = String(payment?.cardHolder || "").trim();
        const expiryDate = String(payment?.expiryDate || "").trim();
        const cvv = String(payment?.cvv || "").trim();

        return {
            method: cardNumber ? "Card" : String(payment?.method || "N/A"),
            status: String(payment?.status || "N/A"),
            provider: String(payment?.provider || "N/A"),
            cardNumber: cardNumber ? `**** **** **** ${cardNumber.slice(-4)}` : "N/A",
            cardHolder: cardHolder || "N/A",
            expiryDate: expiryDate || "N/A",
            cvv: cvv ? "***" : "N/A",
        };
    };

    const paymentSummary = getPaymentSummary();

    const getItemImages = (item: any) => {
        if (Array.isArray(item?.images) && item.images.length) return item.images.filter(Boolean);
        if (item?.imageUrl) return [item.imageUrl];
        return [fallbackImage];
    };

    const handlePrevImage = (itemIndex: number, imagesLength: number) => {
        setActiveImageIndex((prev) => ({
            ...prev,
            [itemIndex]: imagesLength
                ? ((prev[itemIndex] ?? 0) - 1 + imagesLength) % imagesLength
                : 0,
        }));
    };

    const handleNextImage = (itemIndex: number, imagesLength: number) => {
        setActiveImageIndex((prev) => ({
            ...prev,
            [itemIndex]: imagesLength
                ? ((prev[itemIndex] ?? 0) + 1) % imagesLength
                : 0,
        }));
    };

    if (loading) {
        return (
            <Box p={{ base: 4, md: 6 }} color={theme.text}>
                <Box
                    maxW="7xl"
                    mx="auto"
                    border="1px solid"
                    borderColor={theme.border}
                    bg={theme.surface}
                    borderRadius={cardsBorderRadii}
                    p={6}
                    boxShadow={theme.shadowSm}
                >
                    <HStack spacing={3}>
                        <Spinner size="sm" />
                        <Text>Loading order details...</Text>
                    </HStack>
                </Box>
            </Box>
        );
    }

    if (!order) {
        return (
            <Box p={{ base: 4, md: 6 }} color={theme.text}>
                <Box
                    maxW="700px"
                    mx="auto"
                    border="1px solid"
                    borderColor={theme.border}
                    bg={theme.surface}
                    borderRadius={cardsBorderRadii}
                    p={6}
                    boxShadow={theme.shadowSm}
                >
                    <Text fontSize="lg" fontWeight="bold" color={theme.text}>
                        Order not found
                    </Text>
                    <Text mt={2} color={theme.textSubtle}>
                        We could not find the requested order.
                    </Text>

                    <Button
                        mt={5}
                        onClick={() => navigate(-1)}
                        variant="outline"
                        borderColor={theme.borderStrong}
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                    >
                        Go back
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box p={{ base: 4, md: 6 }} color={theme.text}>
            <Box maxW="full" mx="auto">
                <VStack align="stretch" spacing={6}>
                    <Box
                        border="1px solid"
                        borderColor={theme.border}
                        borderRadius={cardsBorderRadii}
                        px={{ base: 5, md: 7 }}
                        py={{ base: 5, md: 6 }}
                        bg={`linear-gradient(135deg, ${theme.surface} 0%, ${theme.surfaceAlt} 100%)`}
                        boxShadow={theme.shadowSm}
                    >
                        <Flex
                            justify="space-between"
                            align={{ base: "start", lg: "center" }}
                            direction={{ base: "column", lg: "row" }}
                            gap={5}
                        >
                            <VStack align="start" spacing={2}>
                                <HStack spacing={3} flexWrap="wrap">
                                    <Text
                                        fontSize={{ base: "2xl", md: "3xl" }}
                                        fontWeight="extrabold"
                                        color={theme.text}
                                        lineHeight="1.1"
                                    >
                                        Order Details
                                    </Text>
                                    <Badge
                                        colorScheme={statusBadge.colorScheme}
                                        px={3}
                                        py={1}
                                        borderRadius={cardsBorderRadii}
                                        fontSize="0.78rem"
                                    >
                                        {statusBadge.label}
                                    </Badge>
                                </HStack>

                                <Text color={theme.textSubtle}>
                                    Order ID: {order?._id || order?.id || "-"}
                                </Text>

                                <HStack spacing={3} flexWrap="wrap">
                                    <MiniStat label="Order Status" value={statusBadge.label} />
                                    <MiniStat label="Payment Status" value={paymentSummary.status} />
                                    <MiniStat label="Items" value={String(order?.items?.length || 0)} />
                                    <MiniStat label="Category" value={order?.category || "-"} />
                                    <MiniStat label="Currency" value={order?.currency || "MWK"} />
                                </HStack>
                            </VStack>

                            <VStack align={{ base: "start", lg: "end" }} spacing={2}>
                                <Text fontSize="sm" color={theme.textSubtle}>
                                    Total Amount
                                </Text>
                                <Text
                                    fontSize={{ base: "2xl", md: "3xl" }}
                                    fontWeight="extrabold"
                                    color={theme.text}
                                    lineHeight="1"
                                >
                                    {formatMoney(order?.total)}
                                </Text>

                                <Button
                                    onClick={() => navigate(-1)}
                                    variant="outline"
                                    borderColor={theme.borderStrong}
                                    color={theme.text}
                                    _hover={{ bg: theme.hover }}
                                    borderRadius={cardsBorderRadii}
                                >
                                    Back
                                </Button>
                            </VStack>
                        </Flex>
                    </Box>

                    <SimpleGrid columns={{ base: 1, xl: 12 }} spacing={6} alignItems="start">
                        <Box gridColumn={{ base: "span 1", xl: "span 7" }}>
                            <SectionCard title="Ordered Items">
                                <Stack spacing={5}>
                                    {(order?.items || []).map((item: any, idx: number) => {
                                        const images = getItemImages(item);
                                        const active = activeImageIndex[idx] ?? 0;
                                        const currentImage = images[active] || fallbackImage;
                                        const subtotal =
                                            Number(item?.price || 0) * Number(item?.quantity || 1);

                                        return (
                                            <Box
                                                key={`${item?._id || item?.id || item?.title || "item"}-${idx}`}
                                                border="1px solid"
                                                borderColor={theme.border}
                                                borderRadius={cardsBorderRadii}
                                                p={{ base: 4, md: 5 }}
                                                bg={theme.surfaceAlt}
                                                boxShadow="sm"
                                            >
                                                <SimpleGrid
                                                    columns={{ base: 1, md: hideItemImages ? 1 : 2 }}
                                                    spacing={{ base: 5, md: 6 }}
                                                    alignItems="start"
                                                >
                                                    {!hideItemImages && <VStack align="stretch" spacing={3}>
                                                        <Box
                                                            position="relative"
                                                            borderWidth="1px"
                                                            borderColor={theme.border}
                                                            borderRadius={cardsBorderRadii}
                                                            overflow="hidden"
                                                            h={{ base: "240px", md: "290px" }}
                                                            bg={theme.surface}
                                                        >
                                                            <Image
                                                                src={currentImage}
                                                                alt={item?.title || "Order item"}
                                                                w="100%"
                                                                h="100%"
                                                                objectFit="cover"
                                                            />

                                                            {images.length > 1 && (
                                                                <>
                                                                    <IconButton
                                                                        aria-label="Previous image"
                                                                        icon={<ChevronLeftIcon />}
                                                                        size="sm"
                                                                        position="absolute"
                                                                        top="50%"
                                                                        left={3}
                                                                        transform="translateY(-50%)"
                                                                        onClick={() => handlePrevImage(idx, images.length)}
                                                                        borderRadius={cardsBorderRadii}
                                                                        bg="blackAlpha.600"
                                                                        color="white"
                                                                        _hover={{ bg: "blackAlpha.700" }}
                                                                    />
                                                                    <IconButton
                                                                        aria-label="Next image"
                                                                        icon={<ChevronRightIcon />}
                                                                        size="sm"
                                                                        position="absolute"
                                                                        top="50%"
                                                                        right={3}
                                                                        transform="translateY(-50%)"
                                                                        onClick={() => handleNextImage(idx, images.length)}
                                                                        borderRadius={cardsBorderRadii}
                                                                        bg="blackAlpha.600"
                                                                        color="white"
                                                                        _hover={{ bg: "blackAlpha.700" }}
                                                                    />
                                                                </>
                                                            )}
                                                        </Box>

                                                        <HStack justify="space-between">
                                                            <Text fontSize="sm" color={theme.textMuted}>
                                                                {images.length ? active + 1 : 0} / {images.length} images
                                                            </Text>
                                                            <Text fontSize="sm" color={theme.textSubtle}>
                                                                {formatMoney(subtotal)}
                                                            </Text>
                                                        </HStack>

                                                        <HStack spacing={2} overflowX="auto" py={1}>
                                                            {images.map((src: string, imageIdx: number) => (
                                                                <Box
                                                                    key={`${src}-${imageIdx}`}
                                                                    borderWidth={imageIdx === active ? "2px" : "1px"}
                                                                    borderColor={
                                                                        imageIdx === active
                                                                            ? theme.primary
                                                                            : theme.border
                                                                    }
                                                                    borderRadius={cardsBorderRadii}
                                                                    overflow="hidden"
                                                                    cursor="pointer"
                                                                    flex="0 0 auto"
                                                                    onClick={() =>
                                                                        setActiveImageIndex((prev) => ({
                                                                            ...prev,
                                                                            [idx]: imageIdx,
                                                                        }))
                                                                    }
                                                                    bg={theme.surface}
                                                                    transition="all 0.2s ease"
                                                                    transform={
                                                                        imageIdx === active
                                                                            ? "translateY(-2px)"
                                                                            : "none"
                                                                    }
                                                                >
                                                                    <Image
                                                                        src={src}
                                                                        alt={`${item?.title || "Item"}-${imageIdx}`}
                                                                        boxSize="68px"
                                                                        objectFit="cover"
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </HStack>
                                                    </VStack>}

                                                    <VStack align="stretch" spacing={4}>
                                                        <Flex
                                                            justify="space-between"
                                                            align="start"
                                                            gap={3}
                                                            wrap="wrap"
                                                        >
                                                            <VStack align="start" spacing={1} flex="1">
                                                                <Text
                                                                    fontSize="xl"
                                                                    fontWeight="bold"
                                                                    color={theme.text}
                                                                    lineHeight="1.2"
                                                                >
                                                                    {item?.title || "Untitled item"}
                                                                </Text>
                                                                <Text color={theme.textMuted}>
                                                                    {item?.description || "No description provided."}
                                                                </Text>
                                                            </VStack>

                                                            <Badge
                                                                colorScheme="purple"
                                                                px={3}
                                                                py={1}
                                                                borderRadius={cardsBorderRadii}
                                                            >
                                                                Qty: {item?.quantity ?? 1}
                                                            </Badge>
                                                        </Flex>

                                                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                                                            <InfoCard label="Item ID" value={item?._id || item?.id || "-"} />
                                                            <InfoCard label="Product ID" value={item?.productId || "-"} />
                                                            <InfoCard label="Category" value={item?.category || "-"} />
                                                            <InfoCard label="Price" value={formatMoney(item?.price)} />
                                                            <InfoCard label="Quantity" value={String(item?.quantity ?? 1)} />
                                                            <InfoCard label="Subtotal" value={formatMoney(subtotal)} />
                                                        </SimpleGrid>
                                                    </VStack>
                                                </SimpleGrid>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </SectionCard>
                        </Box>

                        <Box
                            gridColumn={{ base: "span 1", xl: "span 5" }}
                            position={{ base: "static", xl: "sticky" }}
                            top="24px"
                        >
                            <VStack align="stretch" spacing={4}>
                                <SectionCard title="Summary">
                                    <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} spacing={3}>
                                        <InfoCard label="Order ID" value={order?._id || order?.id || "-"} />
                                        <InfoCard label="Category" value={order?.category || "-"} />
                                        <InfoCard label="Currency" value={order?.currency || "-"} />
                                        <InfoCard label="Total" value={formatMoney(order?.total)} />
                                        <InfoCard label="Order Status" value={statusBadge.label} />
                                        <InfoCard label="Payment Status" value={paymentSummary.status} />
                                        <InfoCard label="Items Count" value={String(order?.items?.length || 0)} />
                                    </SimpleGrid>
                                </SectionCard>

                                <SectionCard title="Customer Details">
                                    <Stack spacing={3}>
                                        <InfoRow label="User ID" value={order?.userId || "-"} />
                                        <InfoRow label="Email" value={order?.email || "-"} />
                                        <InfoRow label="Phone Number" value={order?.phoneNumber || "-"} />
                                        <InfoRow
                                            label="Points Awarded At"
                                            value={
                                                order?.pointsAwardedAt
                                                    ? new Date(order.pointsAwardedAt).toLocaleString()
                                                    : "Not awarded"
                                            }
                                        />
                                    </Stack>
                                </SectionCard>

                                <SectionCard title="Delivery Details">
                                    <Stack spacing={3}>
                                        <InfoBlock label="From Address" value={order?.fromAddress || "—"} />
                                        <InfoBlock label="To Address" value={order?.toAddress || "—"} />
                                        <InfoBlock
                                            label="Delivery Instructions"
                                            value={order?.deliveryInstructions || "—"}
                                            whiteSpace="pre-wrap"
                                        />
                                    </Stack>
                                </SectionCard>

                                <SectionCard title="Payment Details">
                                    <Stack spacing={3}>
                                        <InfoRow label="Method" value={paymentSummary.method} />
                                        <InfoRow label="Status" value={paymentSummary.status} />
                                        <InfoRow label="Provider" value={paymentSummary.provider} />
                                        <InfoRow label="Card Number" value={paymentSummary.cardNumber} />
                                        <InfoRow label="Card Holder" value={paymentSummary.cardHolder} />
                                        <InfoRow label="Expiry Date" value={paymentSummary.expiryDate} />
                                        <InfoRow label="CVV" value={paymentSummary.cvv} />
                                    </Stack>
                                </SectionCard>

                                <SectionCard title="Timestamps">
                                    <Stack spacing={3}>
                                        <InfoRow
                                            label="Created At"
                                            value={order?.createdAt ? new Date(order.createdAt).toLocaleString() : "—"}
                                        />
                                        <InfoRow
                                            label="Updated At"
                                            value={order?.updatedAt ? new Date(order.updatedAt).toLocaleString() : "—"}
                                        />
                                    </Stack>
                                </SectionCard>
                            </VStack>
                        </Box>
                    </SimpleGrid>
                </VStack>
            </Box>
        </Box>
    );
};

function SectionCard({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) {
    const { theme } = useCustomTheme();

    return (
        <Box
            borderWidth="1px"
            borderColor={theme.border}
            borderRadius={cardsBorderRadii}
            p={{ base: 4, md: 5 }}
            bg={theme.surface}
            boxShadow={theme.shadowSm}
        >
            <Text
                fontSize="lg"
                fontWeight="bold"
                mb={4}
                color={theme.text}
                letterSpacing="-0.01em"
            >
                {title}
            </Text>
            {children}
        </Box>
    );
}

function MiniStat({ label, value }: { label: string; value?: string; }) {
    const { theme } = useCustomTheme();

    return (
        <Box
            px={3}
            py={2}
            borderRadius={cardsBorderRadii}
            bg={theme.surfaceAlt}
            border="1px solid"
            borderColor={theme.border}
        >
            <Text fontSize="xs" color={theme.textSubtle}>
                {label}
            </Text>
            <Text fontWeight="bold" color={theme.text}>
                {value || "—"}
            </Text>
        </Box>
    );
}

function InfoCard({ label, value }: { label: string; value?: string; }) {
    const { theme } = useCustomTheme();

    return (
        <Box
            borderWidth="1px"
            borderColor={theme.border}
            borderRadius={cardsBorderRadii}
            p={3}
            bg={theme.surfaceAlt}
            minH="76px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
        >
            <Text fontSize="xs" color={theme.textSubtle} mb={1}>
                {label}
            </Text>
            <Text fontWeight="semibold" color={theme.text} wordBreak="break-word">
                {value || "—"}
            </Text>
        </Box>
    );
}

function InfoBlock({
    label,
    value,
    whiteSpace,
}: {
    label: string;
    value?: string;
    whiteSpace?: any;
}) {
    const { theme } = useCustomTheme();

    return (
        <Box
            p={4}
            borderWidth="1px"
            borderColor={theme.border}
            borderRadius={cardsBorderRadii}
            bg={theme.surfaceAlt}
        >
            <Text fontSize="sm" color={theme.textSubtle} mb={1}>
                {label}
            </Text>
            <Text fontWeight="semibold" color={theme.text} whiteSpace={whiteSpace}>
                {value || "—"}
            </Text>
        </Box>
    );
}

function InfoRow({ label, value }: { label: string; value?: string; }) {
    const { theme } = useCustomTheme();

    return (
        <>
            <Flex justify="space-between" align="start" gap={4} wrap="wrap">
                <Text color={theme.textSubtle}>{label}</Text>
                <Text fontWeight="semibold" color={theme.text} textAlign="right">
                    {value || "—"}
                </Text>
            </Flex>
            <Divider borderColor={theme.divider} />
        </>
    );
}

export default OrderDetail;
